import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { NoirBg } from "@/components/NoirBg";
import { ChatList, Avatar } from "@/components/community/ChatList";
import { ChatWindow } from "@/components/community/ChatWindow";
import { TermsGate } from "@/components/community/TermsGate";
import { GlassField, GlassButton } from "@/components/GlassBits";
import { useCommunitySocket } from "@/hooks/useCommunitySocket";
import { useCalls } from "@/hooks/useCalls";
import { CallOverlay } from "@/components/community/CallOverlay";
import { Loader2, Layers, Users, Plus, Search, Wand2, X, Newspaper, Store } from "lucide-react";

export default function Community() {
  const [me, setMe] = useState(null);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [typing, setTyping] = useState({});
  const [bump, setBump] = useState(0);
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [groupMode, setGroupMode] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [picked, setPicked] = useState([]);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await userApi.get("/community/conversations");
      setConvs(data);
      setActive((a) => (a ? data.find((c) => c.conv_id === a.conv_id) || a : a));
    } catch (e) { toast.error(apiError(e, "No se pudieron cargar los chats")); }
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await userApi.get("/community/me");
      setMe(data);
      if (!data.needs_terms && !data.blocked) await loadConvs();
    } catch (e) { toast.error(apiError(e)); }
  }, [loadConvs]);

  useEffect(() => { loadMe(); }, [loadMe]);

  const calls = useCalls();

  useCommunitySocket((ev) => {
    if (String(ev.event || "").startsWith("call:")) return calls.handleEvent(ev);
    if (ev.event === "message" || ev.event === "message_edited" || ev.event === "message_deleted") {
      setBump((b) => b + 1);
      loadConvs();
    }
    if (ev.event === "typing") {
      setTyping((t) => ({ ...t, [ev.conv_id]: ev.name }));
      setTimeout(() => setTyping((t) => ({ ...t, [ev.conv_id]: null })), 3500);
    }
    if (ev.event === "conversation") loadConvs();
  });

  const openMembers = async () => {
    setMembers([]);
    try {
      const { data } = await userApi.get("/community/members", { params: { q } });
      setMembers(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const startDm = async (key) => {
    try {
      const { data } = await userApi.post("/community/conversations", { type: "dm", member: key });
      setMembers(null);
      await loadConvs();
      setActive(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const createGroup = async () => {
    if (!groupTitle.trim() || picked.length === 0) return toast.error("Ponle nombre y elige miembros");
    try {
      const { data } = await userApi.post("/community/conversations",
        { type: "group", title: groupTitle.trim(), members: picked });
      setGroupMode(false); setGroupTitle(""); setPicked([]); setMembers(null);
      await loadConvs();
      setActive(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center noir-bg theme-noir" style={{ backgroundColor: "#050507" }}>
        <NoirBg />
        <Loader2 className="w-7 h-7 animate-spin text-red-400 relative z-10" />
      </div>
    );
  }

  return (
    <div className="min-h-screen noir-bg theme-noir flex flex-col" style={{ backgroundColor: "#050507" }}>
      <NoirBg />

      <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-xl"
        style={{ background: "rgba(10,3,4,0.55)" }}>
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo-digital-forge.jpg" alt="Digital Forge" data-testid="brand-logo"
              className="w-9 h-9 rounded-md object-cover shrink-0 border border-[#FF8A2B]/40"
              style={{ boxShadow: "0 0 14px rgba(255,138,43,0.28)" }} />
            <div className="leading-tight min-w-0">
              <div className="font-heading font-bold text-sm text-white">Comunidad</div>
              <div className="text-[10px] text-white/45 font-mono truncate">{me.profile?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/mercado" data-testid="community-to-market"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Store className="w-3.5 h-3.5" /> Mercado
            </Link>
            <Link to="/muro" data-testid="community-to-feed"
              className="glass-btn glass-btn-red px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Newspaper className="w-3.5 h-3.5" /> Muro
            </Link>
            <Link to="/panel" data-testid="community-to-editor"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Wand2 className="w-3.5 h-3.5" /> Editor
            </Link>
            <Avatar person={me.profile} size={34} />
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 max-w-7xl w-full mx-auto px-4 py-4">
        {me.blocked ? (
          <div className="glass-card p-7 max-w-lg mx-auto text-white/75" data-testid="community-blocked">
            Tu acceso a la comunidad fue retirado por el administrador.
          </div>
        ) : me.needs_terms ? (
          <TermsGate onAccepted={loadMe} />
        ) : (
          <div className="grid lg:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-7rem)]">
            <aside className={`glass-card p-3 overflow-y-auto ${active ? "hidden lg:block" : ""}`}>
              <div className="flex gap-2 mb-3">
                <GlassButton tone="neutral" data-testid="open-members-btn" icon={Users}
                  onClick={openMembers}>Miembros</GlassButton>
                <GlassButton tone="red" data-testid="new-group-btn" icon={Plus}
                  onClick={() => { setGroupMode(true); openMembers(); }}>Grupo</GlassButton>
              </div>
              <ChatList conversations={convs} activeId={active?.conv_id} typing={typing}
                onSelect={(c) => setActive(c)} />
            </aside>

            <section className={`glass-card overflow-hidden ${active ? "" : "hidden lg:block"}`}>
              {active ? (
                <ChatWindow conv={active} me={me.profile.key} isAdmin={me.profile.is_admin}
                  typingName={typing[active.conv_id]} bump={bump}
                  onCall={calls.startCall}
                  onBack={() => setActive(null)} refreshList={loadConvs} />
              ) : (
                <div className="h-full flex items-center justify-center text-white/45 text-sm px-6 text-center">
                  Elige un canal o chat para empezar a platicar
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      {members !== null && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: "rgba(3,0,0,0.7)" }} data-testid="members-modal">
          <div className="glass-card w-full max-w-md p-5 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="text-white font-semibold">{groupMode ? "Nuevo grupo" : "Miembros"}</div>
              <button data-testid="close-members" onClick={() => { setMembers(null); setGroupMode(false); setPicked([]); }}
                className="text-white/60"><X className="w-4 h-4" /></button>
            </div>
            {groupMode && (
              <GlassField label="Nombre del grupo" value={groupTitle} className="mb-3"
                data-testid="group-title-input" placeholder="Ej. Mayoristas"
                onChange={(e) => setGroupTitle(e.target.value)} />
            )}
            <div className="flex gap-2 mb-3">
              <GlassField className="flex-1" value={q} placeholder="Buscar por nombre o correo"
                data-testid="members-search" onChange={(e) => setQ(e.target.value)} />
              <GlassButton tone="neutral" icon={Search} className="w-auto px-3" onClick={openMembers} />
            </div>
            <div className="space-y-2">
              {members.length === 0 && <div className="text-sm text-white/50">Sin miembros todavía.</div>}
              {members.map((p) => (
                <button key={p.key} data-testid={`member-${p.key}`}
                  onClick={() => groupMode
                    ? setPicked((s) => s.includes(p.key) ? s.filter((k) => k !== p.key) : [...s, p.key])
                    : startDm(p.key)}
                  className={`w-full flex items-center gap-3 p-2 rounded-xl border transition-colors ${
                    picked.includes(p.key) ? "border-red-500/60 bg-white/8" : "border-white/10 hover:bg-white/5"
                  }`}>
                  <Avatar person={p} size={34} />
                  <div className="text-left min-w-0">
                    <div className="text-sm text-white truncate">{p.name}</div>
                    <div className="text-[11px] text-white/45 truncate">{p.bio || p.email}</div>
                  </div>
                </button>
              ))}
            </div>
            {groupMode && (
              <GlassButton tone="red" className="mt-4" data-testid="create-group-btn"
                onClick={createGroup}>Crear grupo ({picked.length})</GlassButton>
            )}
          </div>
        </div>
      )}

      <CallOverlay call={calls.call} localStream={calls.localStream}
        remoteStream={calls.remoteStream}
        onAccept={calls.accept} onReject={calls.reject} onHangup={calls.hangup}
        onToggleMute={calls.toggleMute} onToggleCam={calls.toggleCam}
        onToggleShare={calls.toggleShare} />

    </div>
  );
}